import {
  type UpdateAiFieldRunMetadataOptions,
  type WriteAiSummaryCellOptions,
} from '@databases/ai-first-creation-database-agent-types';
import { setValueValue } from '@databases/collab/y-database';
import { type MutableDatabase } from '@databases/model/database';
import { FieldType, isAiFieldType } from '@databases/model/field';
import { DEFAULT_ENTRY_ID } from '@databases/types/database';

const createAiValueMetadata = ({
  actor,
  accountId,
  now = new Date(),
}: Pick<WriteAiSummaryCellOptions, 'actor' | 'accountId' | 'now'>) => ({
  lastUpdatedAt: now.toISOString(),
  lastUpdatedBy: actor,
  ...(actor === 'user' && accountId !== undefined
    ? { lastUpdatedUserAccountId: accountId }
    : {}),
});

const createAiFieldRunMetadata = ({
  actor,
  accountId,
  now = new Date(),
}: Pick<UpdateAiFieldRunMetadataOptions, 'actor' | 'accountId' | 'now'>) => ({
  lastRunAt: now.toISOString(),
  lastRunBy: actor,
  ...(actor === 'user' && accountId !== undefined
    ? { lastRunUserAccountId: accountId }
    : {}),
});

export class AiFieldAgent {
  constructor(private readonly database: MutableDatabase) {}

  public writeAiSummaryCell({
    entryId,
    fieldId,
    value,
    actor,
    accountId,
    now,
  }: WriteAiSummaryCellOptions): void {
    this.database.mutate((db) => {
      const entry =
        entryId === DEFAULT_ENTRY_ID ? db.defaultEntry : db.entriesById[entryId];
      if (!entry) {
        throw new Error(`Entry with ID ${entryId} not found`);
      }

      const field = db.fieldsById[fieldId];
      if (!field) {
        throw new Error(`Field with ID ${fieldId} not found`);
      }

      if (field.type !== FieldType.AiSummary) {
        throw new Error(`Field with ID ${fieldId} is not an AI Summary field`);
      }

      entry.removeValue(fieldId);
      const mutableValue = entry.setValue(fieldId, FieldType.AiSummary);

      setValueValue(mutableValue, {
        type: FieldType.AiSummary,
        value: {
          ...value,
          metadata: createAiValueMetadata({ actor, accountId, now }),
        },
      });
    });
  }

  public updateAiFieldRunMetadata({
    fieldId,
    actor,
    accountId,
    now,
  }: UpdateAiFieldRunMetadataOptions): void {
    this.database.mutate((db) => {
      const field = db.fieldsById[fieldId];
      if (!field) {
        throw new Error(`Field with ID ${fieldId} not found`);
      }

      if (!isAiFieldType(field.type)) {
        throw new Error(`Field with ID ${fieldId} is not an AI field`);
      }

      field.aiRunMetadata = createAiFieldRunMetadata({ actor, accountId, now });
    });
  }
}
